import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * Custom field definitions — per-practice metadata catalog that powers the
 * Custom Fields configuration tab. Two subject types share the catalog:
 *   - client:  values stored on clients.custom_fields (JSONB keyed by def id)
 *   - tx_plan: values stored on tx_plans.custom_fields
 *
 * Staff custom fields used to be a third subject; the surface was retired
 * when PMA's user-administration UI moved to Home (see platform follow-ups).
 *
 * The defs themselves are not PHI — they describe field shape, not content —
 * but archived defs may only be listed by users with manage_settings because
 * they can leak past field names that were dropped deliberately.
 */

export type CustomFieldType =
    | 'text'
    | 'textarea'
    | 'date'
    | 'number'
    | 'select'
    | 'multiselect'
    | 'checkbox';

export type CustomFieldSubject = 'client' | 'tx_plan';

export interface CustomFieldDef {
    id: string;
    label: string;
    type: CustomFieldType;
    options: string[];
    required: boolean;
    sortOrder: number;
    archivedAt: string | null;
}

export interface CustomFieldWritePayload {
    label: string;
    type: CustomFieldType;
    options?: string[];
    required?: boolean;
    sortOrder?: number;
}

export type CustomFieldUpdatePayload = Partial<CustomFieldWritePayload>;

interface CustomFieldsResponse {
    client: CustomFieldDef[];
    tx_plan: CustomFieldDef[];
}

function emptyResponse(): CustomFieldsResponse {
    return { client: [], tx_plan: [] };
}

async function fetchCustomFields(includeArchived: boolean): Promise<CustomFieldsResponse> {
    const params = includeArchived ? { includeArchived: 'true' } : undefined;
    const res = await api.get<Partial<CustomFieldsResponse>>('/custom-fields', { params });
    // Defensive: server guarantees both keys, but coalesce so a partial
    // response doesn't blow up the UI. The legacy `staff` key (if a stale
    // server still returns it) is ignored — the panel is gone.
    return {
        client:  res.data.client  ?? [],
        tx_plan: res.data.tx_plan ?? [],
    };
}

const queryKey = (includeArchived: boolean) =>
    ['custom-fields', includeArchived ? 'all' : 'active'] as const;

/** Active client custom field defs, sorted by sortOrder asc. Used on Client
 *  detail / edit pages to render the per-client value form. */
export function useClientCustomFieldDefs() {
    return useQuery({
        queryKey: queryKey(false),
        queryFn: () => fetchCustomFields(false),
        staleTime: 5 * 60_000,
        select: (data) =>
            [...data.client]
                .filter((d) => !d.archivedAt)
                .sort((a, b) => a.sortOrder - b.sortOrder),
    });
}

/** Active tx_plan custom field defs, sorted by sortOrder asc. Used on the
 *  Plan Builder to render per-plan values, and on the docx export via
 *  `{plan.cf.<slug>}`. */
export function useTxPlanCustomFieldDefs() {
    return useQuery({
        queryKey: queryKey(false),
        queryFn: () => fetchCustomFields(false),
        staleTime: 5 * 60_000,
        select: (data) =>
            [...data.tx_plan]
                .filter((d) => !d.archivedAt)
                .sort((a, b) => a.sortOrder - b.sortOrder),
    });
}

/** Full catalog (optionally including archived defs) for the Configuration
 *  admin UI. Gated by `manage_settings` for `includeArchived=true`; the
 *  server returns 403 otherwise. */
export function useCustomFieldsCatalog(includeArchived: boolean) {
    return useQuery({
        queryKey: queryKey(includeArchived),
        queryFn: () => fetchCustomFields(includeArchived),
        staleTime: 60_000,
        placeholderData: emptyResponse,
    });
}

function invalidateCatalog(qc: ReturnType<typeof useQueryClient>) {
    qc.invalidateQueries({ queryKey: ['custom-fields'] });
}

export function useCreateCustomField(subject: CustomFieldSubject) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: CustomFieldWritePayload) => {
            const res = await api.post<CustomFieldDef>(`/custom-fields/${subject}`, payload);
            return res.data;
        },
        onSuccess: () => invalidateCatalog(qc),
    });
}

export function useUpdateCustomField(subject: CustomFieldSubject) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: CustomFieldUpdatePayload }) => {
            const res = await api.patch<CustomFieldDef>(`/custom-fields/${subject}/${id}`, payload);
            return res.data;
        },
        onSuccess: () => invalidateCatalog(qc),
    });
}

export function useDeleteCustomField(subject: CustomFieldSubject) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/custom-fields/${subject}/${id}`);
        },
        onSuccess: () => invalidateCatalog(qc),
    });
}

export function useRestoreCustomField(subject: CustomFieldSubject) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const res = await api.post<CustomFieldDef>(`/custom-fields/${subject}/${id}/restore`);
            return res.data;
        },
        onSuccess: () => invalidateCatalog(qc),
    });
}

/**
 * Reorder active (non-archived) defs for one subject. Ids must cover every
 * active def for that subject exactly once. Server rewrites each row's
 * sort_order to its position in the array; archived defs keep their stored
 * sort_order. Optimistic update with snapshot rollback on failure.
 */
export function useReorderCustomFields(subject: CustomFieldSubject) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (ids: string[]) => {
            const res = await api.put<CustomFieldDef[]>(`/custom-fields/${subject}/reorder`, { ids });
            return res.data;
        },
        onMutate: async (ids) => {
            await qc.cancelQueries({ queryKey: ['custom-fields'] });
            const snapshots = qc.getQueriesData<CustomFieldsResponse>({ queryKey: ['custom-fields'] });
            for (const [key, snap] of snapshots) {
                if (!snap) continue;
                const subjectRows = snap[subject];
                const byId = new Map(subjectRows.map((r) => [r.id, r]));
                const reordered = ids
                    .map((id, idx) => {
                        const row = byId.get(id);
                        return row ? { ...row, sortOrder: idx + 1 } : null;
                    })
                    .filter((r): r is CustomFieldDef => r !== null);
                const archivedAndMissing = subjectRows.filter((r) => !ids.includes(r.id));
                qc.setQueryData(key, {
                    ...snap,
                    [subject]: [...reordered, ...archivedAndMissing],
                });
            }
            return { snapshots };
        },
        onError: (_err, _ids, ctx) => {
            if (!ctx) return;
            for (const [key, snap] of ctx.snapshots) {
                qc.setQueryData(key, snap);
            }
        },
        onSettled: () => invalidateCatalog(qc),
    });
}

/**
 * The stored shape on a subject record. Values are native JSON types —
 * string / number / boolean / string[] / null — matching the def's type.
 */
export type CustomFieldValue = string | number | boolean | string[] | null;
export type CustomFieldValueMap = Record<string, CustomFieldValue>;
